/**
 * The System page: the server the backend runs on, for administrators and
 * developers.
 *
 * Polls `api/admin/system/` (CPU, memory, GPU, disks and the storage bucket,
 * the backend processes, database, network, deployment) and
 * `api/admin/system/log/` (the tail of the backend log file). The short
 * history behind the sparklines lives in the browser only: it starts when
 * the page opens and is gone when it closes. Visible only to accounts with
 * `is_admin` or `is_developer`; the backend enforces the same rule, this
 * page merely does not render into a 403.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import DeveloperBoardOutlinedIcon from "@mui/icons-material/DeveloperBoardOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import SdStorageOutlinedIcon from "@mui/icons-material/SdStorageOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import axios from "axios";
import { backgroundRequest } from "../helper/sessionExpiry";
import ExplorationPageShell from "../components/ExplorationPageShell";
import { formatDateTime } from "../helper/formatDate";

const STATUS_URL = "api/admin/system/";
const LOG_URL = "api/admin/system/log/";
export const STATUS_INTERVAL_MS = 5000;
export const LOG_INTERVAL_MS = 10000;
/** Samples kept behind the sparklines (5 s each: five minutes). */
const HISTORY_LENGTH = 60;
const LOG_LINE_CHOICES = [100, 300, 1000, 3000];
const LEVELS = ["DEBUG", "INFO", "WARNING", "ERROR", "CRITICAL"];
const LEVEL_RANK = Object.fromEntries(LEVELS.map((level, index) => [level, index]));
/** Minimum level shown in the log panel. */
const LEVEL_FILTERS = [
  { value: "DEBUG", label: "All" },
  { value: "INFO", label: "Info" },
  { value: "WARNING", label: "Warnings" },
  { value: "ERROR", label: "Errors" },
];

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const INK = "#111827";
const INK_MUTED = "#6b7280";
const HAIRLINE = "#e5e7eb";
const ACCENT = "#4C7EFF";

/** Binary units, one decimal: 9.1 GiB. */
export const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) return "—";
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let value = Number(bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
};

/** "4 d 3 h 12 min" from seconds; "—" for nothing. */
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const total = Math.max(0, Math.floor(Number(seconds)));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const parts = [];
  if (days) parts.push(`${days} d`);
  if (days || hours) parts.push(`${hours} h`);
  parts.push(`${minutes} min`);
  return parts.join(" ");
};

const percentText = (value) =>
  value === null || value === undefined ? "—" : `${Math.round(Number(value))} %`;

/** Load colour: calm below 70 %, warning to 90 %, critical above. */
export const loadColor = (percent) => {
  if (percent === null || percent === undefined) return "#9ca3af";
  if (percent < 70) return ACCENT;
  if (percent < 90) return "#d97706";
  return "#dc2626";
};

/** Wording that goes with loadColor, shown as a status pill. */
export const loadLabel = (percent) => {
  if (percent === null || percent === undefined) return "Unknown";
  if (percent < 70) return "Normal";
  if (percent < 90) return "High";
  return "Critical";
};

/** One line for the storage bucket: its size and object count, or the
 *  state of the measurement (it runs in the background on the server). */
export const bucketText = (bucket) => {
  if (!bucket || !bucket.configured) return "local storage, no bucket";
  if (bucket.bytes !== null && bucket.bytes !== undefined) {
    return `${formatBytes(bucket.bytes)} · ${Number(bucket.objects || 0).toLocaleString()} objects`;
  }
  if (bucket.state === "error") return `not measured: ${bucket.error || "unknown error"}`;
  return "measuring…";
};

const LOG_HEADER = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} \[(\w+)\s*\]/;

/**
 * Lines of the backend log as records: a line that starts with the log
 * format's timestamp and level opens a record; any other line (a traceback,
 * a multi-line message) continues the previous one and takes its level.
 */
export const parseLogLines = (lines) => {
  const records = [];
  let level = "INFO";
  (Array.isArray(lines) ? lines : []).forEach((text, index) => {
    const match = LOG_HEADER.exec(text);
    const header = Boolean(match);
    if (header) level = LEVELS.includes(match[1]) ? match[1] : "INFO";
    records.push({ key: index, text, level, header });
  });
  return records;
};

/** Level colours on the dark log surface. */
const LEVEL_COLORS = {
  DEBUG: "#64748b",
  INFO: "#cbd5e1",
  WARNING: "#fbbf24",
  ERROR: "#f87171",
  CRITICAL: "#f87171",
};

/* ------------------------------------------------------------ building blocks */

/** Icon tints per tile: a soft background with the icon in the full hue. */
const TINTS = {
  blue: { bg: "#eff6ff", fg: "#4C7EFF" },
  violet: { bg: "#f5f3ff", fg: "#7c3aed" },
  emerald: { bg: "#ecfdf5", fg: "#059669" },
  amber: { bg: "#fffbeb", fg: "#d97706" },
  rose: { bg: "#fff1f2", fg: "#e11d48" },
  slate: { bg: "#f1f5f9", fg: "#475569" },
  cyan: { bg: "#ecfeff", fg: "#0891b2" },
};

const CARD_SX = {
  borderRadius: 3,
  border: `1px solid ${HAIRLINE}`,
  bgcolor: "#ffffff",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -16px rgba(15, 23, 42, 0.18)",
  minWidth: 0,
};

function TileIcon({ tint, children }) {
  const colors = TINTS[tint] || TINTS.slate;
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2,
        bgcolor: colors.bg,
        color: colors.fg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        "& svg": { fontSize: 20 },
      }}
    >
      {children}
    </Box>
  );
}

function TileHeading({ title, caption }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3, color: INK }}>
        {title}
      </Typography>
      {caption ? (
        <Typography
          variant="caption"
          sx={{ display: "block", color: INK_MUTED, lineHeight: 1.4, mt: 0.25, wordBreak: "break-word" }}
        >
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}

function Tile({ icon, tint = "slate", title, caption, trailing, children, span = 1 }) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...CARD_SX,
        p: 2.5,
        gridColumn: { xs: "auto", md: span > 1 ? `span ${span}` : "auto" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2 }}>
        <TileIcon tint={tint}>{icon}</TileIcon>
        <TileHeading title={title} caption={caption} />
        {trailing ? <Box sx={{ flexShrink: 0 }}>{trailing}</Box> : null}
      </Stack>
      {children}
    </Paper>
  );
}

/** Small pill with a status dot, coloured like the load bars. */
function StatusPill({ percent }) {
  const color = loadColor(percent);
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.4,
        borderRadius: 99,
        bgcolor: `${color}14`,
        color,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color }} />
      {loadLabel(percent)}
    </Box>
  );
}

/** Headline number of a tile with a line of context beneath it. */
function Hero({ value, detail }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 34, fontWeight: 700, lineHeight: 1, letterSpacing: -0.8, color: INK }}>
        {value}
      </Typography>
      {detail ? <Typography sx={{ fontSize: 13, color: INK_MUTED, mt: 0.75 }}>{detail}</Typography> : null}
    </Box>
  );
}

/** A thin load track. ``label`` names it for assistive technology and, when
 *  ``showLabel`` is set, is printed above it with ``value``. */
function Track({ label, percent, value, showLabel = false, height = 6 }) {
  const width = percent === null || percent === undefined ? 0 : Math.min(100, Math.max(0, percent));
  return (
    <Box sx={{ minWidth: 0 }}>
      {showLabel ? (
        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: 13, color: INK, fontWeight: 500 }} noWrap>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 13, color: INK_MUTED, flexShrink: 0 }}>{value ?? percentText(percent)}</Typography>
        </Stack>
      ) : null}
      <Box
        role="progressbar"
        aria-label={label}
        aria-valuenow={Math.round(width)}
        aria-valuemin={0}
        aria-valuemax={100}
        sx={{ height, borderRadius: 99, bgcolor: "#f1f5f9", overflow: "hidden" }}
      >
        <Box
          sx={{ width: `${width}%`, height: "100%", borderRadius: 99, bgcolor: loadColor(percent), transition: "width 300ms" }}
        />
      </Box>
    </Box>
  );
}

/** One series over the last samples, 0–100: a soft area under a 2 px line. */
function Sparkline({ points, label }) {
  const path = useMemo(() => {
    const values = points.filter((value) => value !== null && value !== undefined);
    if (values.length < 2) return null;
    const step = 100 / (HISTORY_LENGTH - 1);
    const offset = 100 - step * (values.length - 1);
    const coords = values.map((value, index) => [
      offset + index * step,
      30 - (Math.min(100, Math.max(0, value)) / 100) * 27 - 1.5,
    ]);
    const line = coords.map(([x, y], index) => `${index ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`).join(" ");
    const area = `${line} L${coords[coords.length - 1][0].toFixed(2)} 30 L${coords[0][0].toFixed(2)} 30 Z`;
    return { line, area };
  }, [points]);
  const gradientId = useMemo(() => `spark-${Math.random().toString(36).slice(2, 8)}`, []);
  return (
    <Box sx={{ mt: 1.5, mb: 1.5 }}>
      <Box
        component="svg"
        viewBox="0 0 100 30"
        preserveAspectRatio="none"
        role="img"
        aria-label={label}
        sx={{ width: "100%", height: 56, display: "block" }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ACCENT} stopOpacity="0.28" />
            <stop offset="100%" stopColor={ACCENT} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[7.5, 15, 22.5].map((y) => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="#f1f5f9" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        ))}
        <line x1="0" y1="29.5" x2="100" y2="29.5" stroke={HAIRLINE} strokeWidth="1" vectorEffect="non-scaling-stroke" />
        {path ? (
          <>
            <path d={path.area} fill={`url(#${gradientId})`} />
            <path
              d={path.line}
              fill="none"
              stroke={ACCENT}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </>
        ) : null}
      </Box>
      <Typography sx={{ fontSize: 11, color: INK_MUTED, mt: 0.5 }}>{label}</Typography>
    </Box>
  );
}

/** Label-above-value cells in two columns; ``wide`` cells take the row. */
function Facts({ items }) {
  return (
    <Box
      sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", columnGap: 2, rowGap: 1.5, mt: 0.5 }}
    >
      {items.map((item) => (
        <Box key={item.label} sx={{ gridColumn: item.wide ? "1 / -1" : "auto", minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: INK_MUTED }}
          >
            {item.label}
          </Typography>
          {item.children || (
            <Typography
              title={item.title || (typeof item.value === "string" ? item.value : undefined)}
              sx={{ fontSize: 13.5, fontWeight: 600, color: INK, mt: 0.25, wordBreak: "break-word", lineHeight: 1.4 }}
            >
              {item.value}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function Divider() {
  return <Box sx={{ borderTop: `1px solid ${HAIRLINE}`, my: 2 }} />;
}

/* ----------------------------------------------------------------- tiles */

function CpuTile({ cpu, history }) {
  if (!cpu) return null;
  const load = cpu.load_average;
  const cores = `${cpu.logical ?? "—"} logical${cpu.physical ? ` / ${cpu.physical} physical` : ""}`;
  return (
    <Tile icon={<SpeedOutlinedIcon />} tint="blue" title="CPU" caption={cores} trailing={<StatusPill percent={cpu.percent} />}>
      <Hero value={percentText(cpu.percent)} detail="of all cores, right now" />
      <Track label="Total" percent={cpu.percent} />
      <Sparkline points={history.map((sample) => sample.cpu)} label="Last five minutes" />
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end", height: 28 }}>
        {(cpu.per_core_percent || []).map((percent, index) => (
          <Tooltip key={index} title={`Core ${index}: ${percentText(percent)}`}>
            <Box
              sx={{
                flex: 1,
                height: "100%",
                bgcolor: "#f1f5f9",
                borderRadius: 1,
                display: "flex",
                alignItems: "flex-end",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: `${Math.max(4, Math.min(100, percent))}%`,
                  bgcolor: loadColor(percent),
                  transition: "height 300ms",
                }}
              />
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Typography sx={{ fontSize: 11, color: INK_MUTED, mt: 0.5 }}>Per core</Typography>
      <Divider />
      <Facts
        items={[
          { label: "Load (1 / 5 / 15 min)", value: load ? `${load["1m"]} / ${load["5m"]} / ${load["15m"]}` : "—" },
          {
            label: "Frequency",
            value: cpu.frequency
              ? `${cpu.frequency.current_mhz} MHz${cpu.frequency.max_mhz ? ` of ${cpu.frequency.max_mhz}` : ""}`
              : "—",
          },
        ]}
      />
    </Tile>
  );
}

function MemoryTile({ memory, history }) {
  if (!memory) return null;
  const swap = memory.swap || {};
  return (
    <Tile
      icon={<MemoryOutlinedIcon />}
      tint="violet"
      title="Memory"
      caption={`${formatBytes(memory.total_bytes)} installed`}
      trailing={<StatusPill percent={memory.percent} />}
    >
      <Hero
        value={percentText(memory.percent)}
        detail={`${formatBytes(memory.used_bytes)} of ${formatBytes(memory.total_bytes)} in use`}
      />
      <Track label="Used" percent={memory.percent} />
      <Sparkline points={history.map((sample) => sample.memory)} label="Last five minutes" />
      <Divider />
      <Facts
        items={[
          { label: "Available", value: formatBytes(memory.available_bytes) },
          {
            label: "Swap",
            value: swap.total_bytes ? `${formatBytes(swap.used_bytes)} of ${formatBytes(swap.total_bytes)}` : "none",
          },
        ]}
      />
    </Tile>
  );
}

function GpuTile({ gpu, history }) {
  if (!gpu) return null;
  if (!gpu.available) {
    return (
      <Tile icon={<DeveloperBoardOutlinedIcon />} tint="emerald" title="GPU">
        <Typography sx={{ fontSize: 13, color: INK_MUTED }}>{gpu.reason || "No GPU."}</Typography>
      </Tile>
    );
  }
  return (
    <>
      {gpu.gpus.map((card, index) => {
        const memoryPercent =
          card.memory_total_mib ? (100 * (card.memory_used_mib || 0)) / card.memory_total_mib : null;
        return (
          <Tile
            key={card.uuid || index}
            icon={<DeveloperBoardOutlinedIcon />}
            tint="emerald"
            title={gpu.gpus.length > 1 ? `GPU ${card.index}` : "GPU"}
            caption={`${card.name}${card.driver_version ? ` · driver ${card.driver_version}` : ""}`}
            trailing={<StatusPill percent={card.utilization_percent} />}
          >
            <Hero value={percentText(card.utilization_percent)} detail="utilisation, right now" />
            <Track label="Utilisation" percent={card.utilization_percent} />
            {index === 0 ? (
              <Sparkline points={history.map((sample) => sample.gpu)} label="Last five minutes" />
            ) : (
              <Box sx={{ mb: 2 }} />
            )}
            <Track
              label="Memory"
              percent={memoryPercent}
              value={`${card.memory_used_mib ?? "—"} of ${card.memory_total_mib ?? "—"} MiB`}
              showLabel
            />
            <Divider />
            <Facts
              items={[
                {
                  label: "Temperature",
                  value:
                    card.temperature_c === null || card.temperature_c === undefined ? "—" : `${card.temperature_c} °C`,
                },
                {
                  label: "Power",
                  value:
                    card.power_draw_w === null || card.power_draw_w === undefined
                      ? "—"
                      : `${Math.round(card.power_draw_w)} W${card.power_limit_w ? ` of ${Math.round(card.power_limit_w)} W` : ""}`,
                },
                {
                  label: "Processes on the GPU",
                  wide: true,
                  children:
                    card.processes.length === 0 ? (
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INK, mt: 0.25 }}>none</Typography>
                    ) : (
                      <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75, mt: 0.5 }}>
                        {card.processes.map((proc) => (
                          <Chip
                            key={`${proc.pid}-${proc.memory_used_mib}`}
                            size="small"
                            label={`${proc.pid}${proc.name ? ` ${proc.name}` : ""}: ${proc.memory_used_mib ?? "—"} MiB`}
                            sx={{
                              bgcolor: TINTS.emerald.bg,
                              color: TINTS.emerald.fg,
                              fontWeight: 600,
                              fontFamily: MONO,
                              fontSize: 12,
                            }}
                          />
                        ))}
                      </Stack>
                    ),
                },
              ]}
            />
          </Tile>
        );
      })}
    </>
  );
}

function StorageTile({ disks, bucket }) {
  if (!disks) return null;
  const worst = disks.reduce((max, disk) => Math.max(max, disk.percent || 0), 0);
  const bucketCaption =
    bucket?.configured && bucket.measured_at
      ? `${bucket.state === "measuring" ? "refreshing · " : ""}measured ${formatDateTime(bucket.measured_at)}`
      : null;
  return (
    <Tile
      icon={<SdStorageOutlinedIcon />}
      tint="amber"
      title="Storage"
      caption={`${disks.length} file system${disks.length === 1 ? "" : "s"}${bucket?.configured ? " and the bucket" : ""}`}
      trailing={disks.length ? <StatusPill percent={worst} /> : null}
    >
      {disks.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: INK_MUTED }}>No disks reported.</Typography>
      ) : (
        <Stack spacing={2}>
          {disks.map((disk) => (
            <Track
              key={`${disk.device}-${disk.mountpoint}`}
              label={`${disk.mountpoint} (${disk.fstype})`}
              percent={disk.percent}
              value={`${formatBytes(disk.used_bytes)} of ${formatBytes(disk.total_bytes)}`}
              showLabel
              height={8}
            />
          ))}
        </Stack>
      )}
      {bucket ? (
        <>
          <Divider />
          <Facts
            items={[
              {
                label: bucket.configured ? `Bucket ${bucket.name}` : "Bucket",
                wide: true,
                children: (
                  <>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INK, mt: 0.25, wordBreak: "break-word" }}>
                      {bucketText(bucket)}
                    </Typography>
                    {bucketCaption ? (
                      <Typography sx={{ fontSize: 11, color: INK_MUTED, mt: 0.25 }}>{bucketCaption}</Typography>
                    ) : null}
                  </>
                ),
              },
            ]}
          />
        </>
      ) : null}
    </Tile>
  );
}

const ROLE_STYLES = {
  master: { bg: TINTS.slate.bg, fg: TINTS.slate.fg },
  worker: { bg: TINTS.blue.bg, fg: TINTS.blue.fg },
  server: { bg: TINTS.blue.bg, fg: TINTS.blue.fg },
};

const STATUS_DOTS = { running: "#059669", sleeping: "#94a3b8", "disk-sleep": "#d97706", zombie: "#e11d48", stopped: "#e11d48" };

const headCellSx = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: INK_MUTED,
  borderBottom: `1px solid ${HAIRLINE}`,
  px: 1,
  py: 1,
  whiteSpace: "nowrap",
};
const bodyCellSx = { fontSize: 13, color: INK, borderBottom: "1px solid #f1f5f9", px: 1, py: 1.1, whiteSpace: "nowrap" };

function ProcessesTile({ backend }) {
  if (!backend) return null;
  const rows = backend.processes || [];
  const rss = rows.reduce((sum, row) => sum + (row.rss_bytes || 0), 0);
  return (
    <Tile
      icon={<AccountTreeOutlinedIcon />}
      tint="cyan"
      title="Backend processes"
      caption={`${backend.server} · ${rows.length} process${rows.length === 1 ? "" : "es"} · ${formatBytes(rss)} resident`}
      span={2}
    >
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>PID</TableCell>
              <TableCell sx={headCellSx}>Role</TableCell>
              <TableCell sx={headCellSx} align="right">
                CPU
              </TableCell>
              <TableCell sx={headCellSx} align="right">
                Memory
              </TableCell>
              <TableCell sx={headCellSx} align="right">
                Threads
              </TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={headCellSx}>Started</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const role = ROLE_STYLES[row.role] || ROLE_STYLES.server;
              return (
                <TableRow key={row.pid} sx={{ bgcolor: row.is_responder ? "#f8fafc" : "transparent" }}>
                  <TableCell sx={{ ...bodyCellSx, fontFamily: MONO }}>{row.pid}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        component="span"
                        sx={{ px: 1, py: 0.25, borderRadius: 99, bgcolor: role.bg, color: role.fg, fontSize: 12, fontWeight: 600 }}
                      >
                        {row.role}
                      </Box>
                      {row.is_responder ? (
                        <Typography component="span" sx={{ fontSize: 12, color: INK_MUTED }}>
                          answered this request
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontFamily: MONO }} align="right">
                    {percentText(row.cpu_percent)}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontFamily: MONO }} align="right">
                    {formatBytes(row.rss_bytes)}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontFamily: MONO }} align="right">
                    {row.threads}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: STATUS_DOTS[row.status] || "#94a3b8" }} />
                      <span>{row.status}</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: INK_MUTED }}>{formatDateTime(row.started)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Tile>
  );
}

function HostTile({ host }) {
  if (!host) return null;
  return (
    <Tile icon={<DnsOutlinedIcon />} tint="slate" title="Host" caption="The machine this backend runs on">
      <Facts
        items={[
          { label: "Hostname", value: host.hostname },
          { label: "Python / Django", value: `${host.python} / ${host.django}` },
          { label: "Platform", value: host.platform, wide: true },
          {
            label: "Up since",
            value: `${formatDateTime(host.boot_time)} (${formatDuration(host.uptime_seconds)})`,
            wide: true,
          },
          { label: "Server time", value: formatDateTime(host.server_time), wide: true },
        ]}
      />
    </Tile>
  );
}

function DatabaseTile({ database }) {
  if (!database) return null;
  const version = (database.version || "").split(" on ")[0];
  return (
    <Tile
      icon={<StorageOutlinedIcon />}
      tint="rose"
      title="Database"
      caption={database.vendor}
      trailing={
        database.connections !== undefined && database.max_connections ? (
          <StatusPill percent={(100 * database.connections) / database.max_connections} />
        ) : null
      }
    >
      {!database.reachable ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          Database unreachable{database.error ? `: ${database.error}` : "."}
        </Alert>
      ) : null}
      <Facts
        items={[
          { label: "Database", value: database.name || "—" },
          { label: "Size", value: formatBytes(database.size_bytes) },
          {
            label: "Connections",
            value:
              database.connections === undefined
                ? "—"
                : `${database.connections} (${database.active_connections ?? 0} active) of ${database.max_connections ?? "—"}`,
          },
          { label: "Server", value: version || "—", title: database.version, wide: true },
          ...(database.error && database.reachable ? [{ label: "Note", value: database.error, wide: true }] : []),
        ]}
      />
    </Tile>
  );
}

function NetworkTile({ network }) {
  return (
    <Tile icon={<LanOutlinedIcon />} tint="cyan" title="Network" caption="Totals since boot">
      {network ? (
        <Facts
          items={[
            { label: "Sent", value: `${formatBytes(network.bytes_sent)} · ${network.packets_sent.toLocaleString()} packets` },
            {
              label: "Received",
              value: `${formatBytes(network.bytes_received)} · ${network.packets_received.toLocaleString()} packets`,
            },
          ]}
        />
      ) : (
        <Typography sx={{ fontSize: 13, color: INK_MUTED }}>No counters.</Typography>
      )}
    </Tile>
  );
}

function DeploymentTile({ deployment, backend }) {
  if (!deployment) return null;
  return (
    <Tile icon={<RocketLaunchOutlinedIcon />} tint="violet" title="Deployment" caption={deployment.commit_subject || undefined}>
      <Facts
        items={[
          {
            label: "Backend commit",
            value: deployment.commit ? `${deployment.commit}${deployment.branch ? ` (${deployment.branch})` : ""}` : "—",
            title: deployment.commit_subject || undefined,
          },
          { label: "Committed", value: deployment.commit_date ? formatDateTime(deployment.commit_date) : "—" },
          { label: "Server", value: backend?.server || "—" },
          { label: "Storage", value: deployment.storage === "bucket" ? `bucket ${deployment.bucket}` : "local" },
          {
            label: "Mode",
            value: `${deployment.is_dev_server ? "dev server" : "production"}${deployment.debug ? ", DEBUG on" : ""}`,
          },
          { label: "Log file", value: deployment.log_file || "—", wide: true },
        ]}
      />
    </Tile>
  );
}

/* ------------------------------------------------------------------- log */

function LogPanel({ log, loading, error, lines, onLines, onRefresh }) {
  const [minLevel, setMinLevel] = useState("DEBUG");
  const [search, setSearch] = useState("");
  const [follow, setFollow] = useState(true);
  const boxRef = useRef(null);

  const records = useMemo(() => parseLogLines(log?.lines), [log]);
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const minRank = LEVEL_RANK[minLevel] ?? 0;
    return records.filter(
      (record) => LEVEL_RANK[record.level] >= minRank && (!needle || record.text.toLowerCase().includes(needle))
    );
  }, [records, minLevel, search]);

  useEffect(() => {
    if (follow && boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [shown, follow]);

  const caption = log
    ? log.exists
      ? `${formatBytes(log.size_bytes)} · written ${formatDateTime(log.modified)}`
      : "no log file"
    : "";

  return (
    <Paper elevation={0} sx={{ ...CARD_SX, overflow: "hidden" }}>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", lg: "center" }}
        sx={{ px: 2.5, py: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
          <TileIcon tint="slate">
            <TerminalOutlinedIcon />
          </TileIcon>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3, color: INK }}>
              Backend log
            </Typography>
            <Typography
              variant="caption"
              noWrap
              title={log?.path || ""}
              sx={{ display: "block", color: INK_MUTED, lineHeight: 1.4, mt: 0.25 }}
            >
              {caption}
              {log?.path ? ` · ${log.path}` : ""}
            </Typography>
          </Box>
        </Stack>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={minLevel}
          onChange={(_, value) => value && setMinLevel(value)}
          aria-label="Minimum level"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              px: 1.5,
              fontWeight: 600,
              fontSize: 13,
              borderColor: HAIRLINE,
              color: INK_MUTED,
            },
            "& .Mui-selected": { bgcolor: `${TINTS.blue.bg} !important`, color: `${TINTS.blue.fg} !important` },
          }}
        >
          {LEVEL_FILTERS.map((filter) => (
            <ToggleButton key={filter.value} value={filter.value}>
              {filter.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 180 }}
        />
        <TextField
          size="small"
          select
          label="Lines"
          value={lines}
          onChange={(event) => onLines(Number(event.target.value))}
          sx={{ width: 110 }}
        >
          {LOG_LINE_CHOICES.map((choice) => (
            <MenuItem key={choice} value={choice}>
              {choice}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch size="small" checked={follow} onChange={(event) => setFollow(event.target.checked)} />}
          label="Follow"
          sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 13, fontWeight: 500 } }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={onRefresh}
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Refresh
        </Button>
      </Stack>
      {error ? (
        <Alert severity="error" sx={{ mx: 2.5, mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}
      <Box
        ref={boxRef}
        data-testid="log-lines"
        sx={{
          height: 440,
          overflow: "auto",
          bgcolor: "#0f172a",
          color: LEVEL_COLORS.INFO,
          px: 2.5,
          py: 2,
          fontFamily: MONO,
          fontSize: 12,
          lineHeight: 1.6,
          whiteSpace: "pre",
          borderTop: `1px solid ${HAIRLINE}`,
        }}
      >
        {shown.length === 0 ? (
          <Typography sx={{ fontFamily: "inherit", fontSize: "inherit", color: "#94a3b8" }}>
            {records.length === 0 ? "No lines." : "No lines match the filter."}
          </Typography>
        ) : (
          shown.map((record) => (
            <Box key={record.key} component="div" sx={{ color: LEVEL_COLORS[record.level] || LEVEL_COLORS.INFO }}>
              {record.text}
            </Box>
          ))
        )}
      </Box>
      <Typography sx={{ fontSize: 12, color: INK_MUTED, px: 2.5, py: 1.25 }}>
        {records.length === 0
          ? ""
          : `${shown.length} of ${records.length} lines shown${log?.reached_start ? " (the whole file)" : ""}.`}
      </Typography>
    </Paper>
  );
}

/* ------------------------------------------------------------------ page */

const requestError = (err, what) => {
  const status = err?.response?.status;
  if (status === 403) return "Administrator rights required.";
  if (status === 401) return "Not signed in.";
  const detail = err?.response?.data?.error || err?.response?.data?.detail;
  return detail ? `${what}: ${detail}` : `${what} failed${status ? ` (${status})` : ""}.`;
};

export default function SystemStatus() {
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [history, setHistory] = useState([]);
  const [auto, setAuto] = useState(true);

  const [log, setLog] = useState(null);
  const [logError, setLogError] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [lines, setLines] = useState(LOG_LINE_CHOICES[1]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      // Polling must not count as activity for the session window.
      const res = await axios.get(STATUS_URL, backgroundRequest());
      const data = res?.data || {};
      setStatus(data);
      setStatusError(null);
      setUpdatedAt(new Date());
      setHistory((previous) =>
        [
          ...previous,
          {
            cpu: data.cpu?.percent ?? null,
            memory: data.memory?.percent ?? null,
            gpu: data.gpu?.available && data.gpu.gpus[0] ? data.gpu.gpus[0].utilization_percent : null,
          },
        ].slice(-HISTORY_LENGTH)
      );
    } catch (err) {
      console.error(err);
      setStatusError(requestError(err, "Loading the system status"));
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const res = await axios.get(LOG_URL, backgroundRequest({ params: { lines } }));
      setLog(res?.data || null);
      setLogError(null);
    } catch (err) {
      console.error(err);
      setLogError(requestError(err, "Loading the log"));
    } finally {
      setLogLoading(false);
    }
  }, [lines]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);
  useEffect(() => {
    loadLog();
  }, [loadLog]);

  useEffect(() => {
    if (!auto) return undefined;
    const statusTimer = setInterval(loadStatus, STATUS_INTERVAL_MS);
    const logTimer = setInterval(loadLog, LOG_INTERVAL_MS);
    return () => {
      clearInterval(statusTimer);
      clearInterval(logTimer);
    };
  }, [auto, loadStatus, loadLog]);

  return (
    <Box sx={{ p: 2, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <ExplorationPageShell
        title="System"
        subtitle="Resources of the server this backend runs on, and its log."
        paperSx={{ border: "none", boxShadow: "none", p: 0, bgcolor: "transparent" }}
        headerTrailing={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="caption" sx={{ color: INK_MUTED }}>
              {updatedAt ? `Updated ${updatedAt.toLocaleTimeString()}` : ""}
            </Typography>
            <FormControlLabel
              control={<Switch size="small" checked={auto} onChange={(event) => setAuto(event.target.checked)} />}
              label="Auto-refresh"
              sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 13, fontWeight: 500 } }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={statusLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
              onClick={() => {
                loadStatus();
                loadLog();
              }}
              disabled={statusLoading}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Refresh
            </Button>
          </Stack>
        }
      >
        <Stack spacing={2} sx={{ width: "100%" }}>
          {statusError ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {statusError}
            </Alert>
          ) : null}
          {!status && !statusError ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 4, justifyContent: "center" }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ color: INK_MUTED }}>
                Loading…
              </Typography>
            </Stack>
          ) : null}
          {status ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
              }}
            >
              <CpuTile cpu={status.cpu} history={history} />
              <MemoryTile memory={status.memory} history={history} />
              <GpuTile gpu={status.gpu} history={history} />
              <ProcessesTile backend={status.backend} />
              <StorageTile disks={status.disks} bucket={status.bucket} />
              <HostTile host={status.host} />
              <DatabaseTile database={status.database} />
              <DeploymentTile deployment={status.deployment} backend={status.backend} />
              <NetworkTile network={status.network} />
            </Box>
          ) : null}
          <LogPanel log={log} loading={logLoading} error={logError} lines={lines} onLines={setLines} onRefresh={loadLog} />
        </Stack>
      </ExplorationPageShell>
    </Box>
  );
}
